/**
 * Token Context
 * Provides token state and operations across the application
 */

import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import {
    TokenStats,
    TokenPackage,
    UnlockableFeature,
    AdvancedFeature,
    FeatureStatusMap,
    TOKEN_COSTS,
} from '../types/tokens';
import {
    getTokenStats,
    getAdNonce,
    earnFromAd,
    claimDailyLogin,
    unlockFeature,
    spendOnAdvancedFeature,
    getTokenPackages,
    getFeatureStatus,
} from '../services/tokenService';
import {
    getPendingRewards,
    PendingReward,
} from '../services/referralService';

interface TokenContextType {
    // State
    stats: TokenStats | null;
    packages: TokenPackage[];
    isLoading: boolean;
    error: string | null;
    featureStatus: FeatureStatusMap | null;

    // Referral rewards
    pendingRewards: PendingReward[];
    hasPendingRewards: boolean;
    showRewardAnimation: boolean;

    // Actions
    refreshStats: () => Promise<void>;
    fetchPackages: () => Promise<void>;
    checkPendingRewards: () => Promise<void>;
    dismissRewardAnimation: () => void;
    onRewardsClaimed: (newBalance: number | null) => void;
    refreshFeatureStatus: () => Promise<void>;

    // Earning (nonce-based ad flow)
    requestAdNonce: () => Promise<string>;
    watchAdForTokens: (nonce: string) => Promise<{ earned: number; newBalance: number }>;
    claimDaily: () => Promise<{ earned: number; newBalance: number; streak: number; streakBonus?: number }>;

    // Spending
    unlock: (feature: UnlockableFeature, referenceId?: string) => Promise<boolean>;
    useAdvancedFeature: (feature: AdvancedFeature, referenceId?: string) => Promise<boolean>;

    // Helpers
    canAfford: (cost: number) => boolean;
    getFeatureCost: (feature: UnlockableFeature | AdvancedFeature, isSubscriber: boolean) => number;
}

const TokenContext = createContext<TokenContextType | undefined>(undefined);

interface TokenProviderProps {
    children: ReactNode;
}

export const TokenProvider: React.FC<TokenProviderProps> = ({ children }) => {
    const { user, isAuthenticated } = useAuth();
    const [stats, setStats] = useState<TokenStats | null>(null);
    const [packages, setPackages] = useState<TokenPackage[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [featureStatus, setFeatureStatus] = useState<FeatureStatusMap | null>(null);

    // Referral rewards state
    const [pendingRewards, setPendingRewards] = useState<PendingReward[]>([]);
    const [hasPendingRewards, setHasPendingRewards] = useState(false);
    const [showRewardAnimation, setShowRewardAnimation] = useState(false);
    const [hasCheckedRewards, setHasCheckedRewards] = useState(false);

    const isSubscriber = user?.subscriptionStatus === 'active';

    // Fetch token stats when authenticated
    const refreshStats = useCallback(async () => {
        if (!isAuthenticated) return;

        setIsLoading(true);
        setError(null);
        try {
            const data = await getTokenStats();
            setStats(data);
        } catch (err) {
            setError('Failed to load token stats');
            console.error('Failed to fetch token stats:', err);
        } finally {
            setIsLoading(false);
        }
    }, [isAuthenticated]);

    // Fetch feature status
    const refreshFeatureStatus = useCallback(async () => {
        if (!isAuthenticated) return;
        try {
            const data = await getFeatureStatus();
            setFeatureStatus(data);
        } catch (err) {
            console.error('Failed to fetch feature status:', err);
        }
    }, [isAuthenticated]);

    // Fetch token packages
    const fetchPackages = useCallback(async () => {
        try {
            const data = await getTokenPackages();
            setPackages(data);
        } catch (err) {
            console.error('Failed to fetch token packages:', err);
        }
    }, []);

    // Check for pending referral rewards
    const checkPendingRewards = useCallback(async () => {
        if (!isAuthenticated) return;

        try {
            const result = await getPendingRewards();
            setPendingRewards(result.pendingRewards);
            setHasPendingRewards(result.hasPendingRewards);

            // Show animation if there are pending rewards and we haven't checked before
            if (result.hasPendingRewards && !hasCheckedRewards) {
                setShowRewardAnimation(true);
            }
            setHasCheckedRewards(true);
        } catch (err) {
            console.error('Failed to check pending rewards:', err);
        }
    }, [isAuthenticated, hasCheckedRewards]);

    // Dismiss reward animation
    const dismissRewardAnimation = useCallback(() => {
        setShowRewardAnimation(false);
    }, []);

    // Handle rewards claimed (update balance)
    const onRewardsClaimed = useCallback((newBalance: number | null) => {
        setShowRewardAnimation(false);
        setPendingRewards([]);
        setHasPendingRewards(false);

        if (newBalance !== null) {
            setStats(prev => prev ? { ...prev, balance: newBalance } : null);
        } else {
            // Refresh stats if no new balance provided
            refreshStats();
        }
    }, [refreshStats]);

    // Request ad nonce (call before showing ad)
    const requestAdNonce = useCallback(async (): Promise<string> => {
        const { nonce } = await getAdNonce();
        return nonce;
    }, []);

    // Watch ad for tokens (call after ad completes with the nonce)
    const watchAdForTokens = useCallback(async (nonce: string) => {
        const result = await earnFromAd(nonce);
        setStats(prev => prev ? {
            ...prev,
            balance: result.newBalance,
            totalEarned: prev.totalEarned + result.earned,
            dailyAdTokensRemaining: Math.max(0, prev.dailyAdTokensRemaining - 1),
        } : null);
        return result;
    }, []);

    // Claim daily login bonus
    const claimDaily = useCallback(async () => {
        const result = await claimDailyLogin();
        setStats(prev => prev ? {
            ...prev,
            balance: result.newBalance,
            totalEarned: prev.totalEarned + result.earned,
            loginStreak: result.streak,
            bestLoginStreak: Math.max(prev.bestLoginStreak, result.streak),
            canClaimDailyLogin: false,
        } : null);
        return result;
    }, []);

    // Unlock a feature
    const unlock = useCallback(async (feature: UnlockableFeature, referenceId?: string): Promise<boolean> => {
        try {
            const result = await unlockFeature(feature, referenceId);
            setStats(prev => prev ? { ...prev, balance: result.newBalance } : null);
            // Refresh feature status after unlock
            refreshFeatureStatus();
            return result.unlocked;
        } catch (err) {
            console.error('Failed to unlock feature:', err);
            return false;
        }
    }, [refreshFeatureStatus]);

    // Use advanced feature
    const useAdvancedFeature = useCallback(async (feature: AdvancedFeature, referenceId?: string): Promise<boolean> => {
        try {
            const result = await spendOnAdvancedFeature(feature, referenceId);
            setStats(prev => prev ? { ...prev, balance: result.newBalance } : null);
            return true;
        } catch (err) {
            console.error('Failed to use advanced feature:', err);
            return false;
        }
    }, []);

    // Check if user can afford a cost
    const canAfford = useCallback((cost: number): boolean => {
        return (stats?.balance ?? 0) >= cost;
    }, [stats?.balance]);

    // Get feature cost (considering subscriber discount)
    const getFeatureCost = useCallback((feature: UnlockableFeature | AdvancedFeature, isSubscriberOverride?: boolean): number => {
        const sub = isSubscriberOverride ?? isSubscriber;

        // Basic features are free for subscribers
        const basicFeatures: (UnlockableFeature | AdvancedFeature)[] = ['course', 'dictation', 'shadowing', 'quiz', 'vocabulary'];
        if (sub && basicFeatures.includes(feature)) {
            return 0;
        }

        // Get base cost
        const costMap: Record<string, number> = {
            course: TOKEN_COSTS.UNLOCK_COURSE,
            dictation: TOKEN_COSTS.UNLOCK_DICTATION,
            shadowing: TOKEN_COSTS.UNLOCK_SHADOWING,
            quiz: TOKEN_COSTS.UNLOCK_QUIZ,
            vocabulary: TOKEN_COSTS.UNLOCK_VOCABULARY,
            ai_analysis: TOKEN_COSTS.AI_PRONUNCIATION_ANALYSIS,
            ai_question: TOKEN_COSTS.AI_QUESTION,
            learning_report: TOKEN_COSTS.LEARNING_REPORT,
            streak_freeze: TOKEN_COSTS.STREAK_FREEZE,
            leaderboard_boost: TOKEN_COSTS.LEADERBOARD_BOOST,
        };

        const baseCost = costMap[feature] || 0;

        // Advanced features get 50% discount for subscribers
        const advancedFeatures: (UnlockableFeature | AdvancedFeature)[] = [
            'ai_analysis', 'ai_question', 'learning_report', 'streak_freeze', 'leaderboard_boost'
        ];
        if (sub && advancedFeatures.includes(feature)) {
            return Math.ceil(baseCost * 0.5);
        }

        return baseCost;
    }, [isSubscriber]);

    // Load stats when authenticated
    useEffect(() => {
        if (isAuthenticated) {
            refreshStats();
            refreshFeatureStatus();
            // Check for pending rewards after a short delay to let stats load first
            const timer = setTimeout(() => {
                checkPendingRewards();
            }, 1000);
            return () => clearTimeout(timer);
        } else {
            setStats(null);
            setFeatureStatus(null);
            setPendingRewards([]);
            setHasPendingRewards(false);
            setHasCheckedRewards(false);
        }
    }, [isAuthenticated, refreshStats, refreshFeatureStatus, checkPendingRewards]);

    const value: TokenContextType = {
        stats,
        packages,
        isLoading,
        error,
        featureStatus,
        // Referral rewards
        pendingRewards,
        hasPendingRewards,
        showRewardAnimation,
        // Actions
        refreshStats,
        fetchPackages,
        checkPendingRewards,
        dismissRewardAnimation,
        onRewardsClaimed,
        refreshFeatureStatus,
        requestAdNonce,
        watchAdForTokens,
        claimDaily,
        unlock,
        useAdvancedFeature,
        canAfford,
        getFeatureCost,
    };

    return (
        <TokenContext.Provider value={value}>
            {children}
        </TokenContext.Provider>
    );
};

export const useTokens = (): TokenContextType => {
    const context = useContext(TokenContext);
    if (!context) {
        throw new Error('useTokens must be used within a TokenProvider');
    }
    return context;
};
